import { FlexColumnContainer } from '@ui-components/global/CommonStyling'
import { StandardShadow } from '@ui-components/global/shadow'
import { AdditionalText, BodyText } from '@ui-components/typography/Typography'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { styled } from 'styled-components'

import { Button, ButtonProps } from '../actions/Button'
import { Link } from '../actions/Link'
import { LinkButton, LinkButtonProps } from '../actions/LinkButton'
import { CloseIcon, IconSize, KebabMenuIcon, LeftChevronIcon } from '../assets/Icons'
import { Colors } from '../global/colors'
import { BREAKPOINT_DESKTOP_PX, STEP_HEADER_HEIGHT, STEP_HEADER_SMALL_HEIGHT } from '../global/constants'
import { SPACE_SMALL, SPACE_XSMALL } from '../global/spacing'
import { useMediaQuery } from '../global/useMediaQuery'
import { QuaternaryHeading } from '../typography/headings/QuaternaryHeading'
import { PAGE_BACK_BUTTON_CLASS, PAGE_TITLE_CLASS } from '../utilities/helpers'

const TopBarContainer = styled(FlexColumnContainer)<{ $hideBoxShadow?: boolean }>`
  ${(props) => (props.$hideBoxShadow ? undefined : StandardShadow)}
`

const TopBarWrapper = styled.div<{ $small?: boolean; $topBarContainerWidth?: number }>`
  background-color: ${Colors.White};
  width: 100%;
  min-height: ${({ $small }) => ($small ? `${STEP_HEADER_SMALL_HEIGHT}px` : `${STEP_HEADER_HEIGHT}px`)};
  display: grid;
  align-items: center;
  grid-template-columns: 36px 1fr 36px;

  @media (min-width: ${BREAKPOINT_DESKTOP_PX}px) {
    grid-template-columns: ${({ $topBarContainerWidth }) =>
      $topBarContainerWidth && $topBarContainerWidth >= BREAKPOINT_DESKTOP_PX && '200px 1fr 200px'};
  }

  svg {
    padding-right: 0;
    height: ${IconSize.SMALL}px;
    box-sizing: content-box;
  }
`

const ItemWrapper = styled.div<{ $noPointerEvents?: boolean; $removeSidePadding?: boolean }>`
  text-align: center;

  h1 {
    margin-top: 0;
  }

  > * {
    pointer-events: ${({ $noPointerEvents }) => $noPointerEvents && `none`};
  }

  &:first-child {
    justify-self: start;
    margin-left: ${({ $removeSidePadding }) => ($removeSidePadding ? 0 : SPACE_SMALL)};
    :hover {
      color: ${Colors.TextDefaultNeutral};
    }
    :active {
      color: ${Colors.TextDefaultNeutral};
    }
  }

  &:nth-child(2) {
    padding: 0 ${SPACE_XSMALL};
  }

  &:last-child {
    justify-self: end;
    margin-right: ${({ $removeSidePadding }) => ($removeSidePadding ? 0 : SPACE_SMALL)};
  }
`

const MenuContent = styled(FlexColumnContainer)`
  background-color: ${Colors.White};
  border-top: 1px solid ${Colors.BorderWeakNeutral};
  padding: 16px 0;
  border-bottom: 1px solid ${Colors.BorderWeakNeutral};
`

const MenuOption = styled(FlexColumnContainer)`
  align-items: center;

  a {
    width: fit-content;
    padding: 0 16px;
    min-height: 40px;
  }
`

const MenuToggleWrapper = styled.div`
  cursor: pointer;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  svg {
    padding: 0;
  }
`

export type SecondaryTopBarMenuOption = {
  text: string
  textColor?: string
  onClick: () => unknown
}

export interface SecondaryTopBarMenuProps {
  menuAriaLabelOpen: string
  menuAriaLabelClose: string
  menuOptions?: SecondaryTopBarMenuOption[]
}

interface ToggleMenuProps extends Omit<SecondaryTopBarMenuProps, 'menuOptions'> {
  isMenuOpen: boolean
  setIsMenuOpen: (open: boolean) => void
  'aria-controls': string
}

export interface SecondaryTopBarProps {
  title?: string
  description?: string
  buttonProps?: ButtonProps
  previousLinkButtonProps?: Pick<LinkButtonProps, 'label' | 'onClick' | 'iconOnly'>
  previousButtonProps?: ButtonProps
  small?: boolean
  menu?: SecondaryTopBarMenuProps
  hideBoxShadow?: boolean
  hideButtonLabelsInMobileBreakpoint?: boolean
  removeSidePadding?: boolean
  titleTagName?: 'h1' | 'h2'
  titleId?: string
  headingRef?: React.RefObject<HTMLDivElement | null>
}

export const SecondaryTopBar = ({
  buttonProps,
  previousLinkButtonProps,
  previousButtonProps,
  title,
  small,
  description,
  menu,
  hideBoxShadow,
  hideButtonLabelsInMobileBreakpoint = true,
  removeSidePadding = false,
  titleTagName = 'h1',
  titleId,
  headingRef,
}: SecondaryTopBarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [topBarWidth, setTopBarWidth] = useState<number | undefined>()
  const toggleMenuId = `toggle-menu-${useId()}`
  const topBarContainerRef = useRef<HTMLDivElement>(null)

  const isDesktopSize = useMediaQuery(`(min-width:${BREAKPOINT_DESKTOP_PX}px)`)
  const useLinkButton = !!previousLinkButtonProps && !previousButtonProps

  const showButtonLabels = !hideButtonLabelsInMobileBreakpoint || isDesktopSize

  const handlePreviousButtonClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    // Prevent default navigation behaviour
    e.preventDefault()

    if (useLinkButton) {
      previousLinkButtonProps?.onClick?.(e as React.MouseEvent<HTMLAnchorElement>)
    } else {
      previousButtonProps?.onClick?.(e as React.MouseEvent<HTMLButtonElement>)
    }
  }

  useLayoutEffect(() => {
    setTopBarWidth(topBarContainerRef.current?.clientWidth)
  }, [])

  const ButtonComponent = useLinkButton ? LinkButton : Button
  const prevButtonProps = useLinkButton ? previousLinkButtonProps : previousButtonProps

  return (
    <TopBarContainer $hideBoxShadow={hideBoxShadow} ref={topBarContainerRef}>
      <TopBarWrapper $small={small} $topBarContainerWidth={topBarWidth}>
        <ItemWrapper $removeSidePadding={removeSidePadding}>
          {prevButtonProps?.onClick && (
            <ButtonComponent
              className={PAGE_BACK_BUTTON_CLASS}
              color="neutral"
              compact={true}
              data-testid="step-header-previous-button"
              icon={<LeftChevronIcon />}
              iconOnly={prevButtonProps.iconOnly || !showButtonLabels}
              label={prevButtonProps.label}
              onClick={handlePreviousButtonClick}
              variant="plain"
            />
          )}
        </ItemWrapper>
        <ItemWrapper aria-live="polite" $noPointerEvents={true}>
          {title && (
            <QuaternaryHeading
              ref={headingRef}
              id={titleId}
              className={PAGE_TITLE_CLASS}
              header={title}
              tagName={titleTagName}
              tabIndex={-1}
            />
          )}
          {description && <AdditionalText>{description}</AdditionalText>}
        </ItemWrapper>
        <ItemWrapper $removeSidePadding={removeSidePadding}>
          {menu && (
            <ToggleMenu
              aria-controls={toggleMenuId}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
              menuAriaLabelOpen={menu.menuAriaLabelOpen}
              menuAriaLabelClose={menu.menuAriaLabelClose}
            />
          )}
          {buttonProps && (
            <Button
              ref={buttonProps.ref}
              color="neutral"
              compact={true}
              data-testid="step-header-info-button"
              icon={buttonProps.icon}
              iconOnly={buttonProps.iconOnly || !showButtonLabels}
              label={buttonProps.label}
              onClick={buttonProps.onClick}
              variant="plain"
            />
          )}
        </ItemWrapper>
      </TopBarWrapper>
      {isMenuOpen && (
        <MenuContent id={toggleMenuId}>
          {menu?.menuOptions?.map((menuOption) => (
            <MenuOption key={menuOption.text}>
              <Link standalone={true} onClick={menuOption.onClick}>
                <BodyText>{menuOption.text}</BodyText>
              </Link>
            </MenuOption>
          ))}
        </MenuContent>
      )}
    </TopBarContainer>
  )
}

const ToggleMenu = ({
  isMenuOpen,
  setIsMenuOpen,
  'aria-controls': ariaControls,
  menuAriaLabelOpen,
  menuAriaLabelClose,
}: ToggleMenuProps) => {
  const onToggle = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  return (
    <MenuToggleWrapper>
      <Button
        iconOnly={true}
        label={isMenuOpen ? menuAriaLabelClose : menuAriaLabelOpen}
        aria-expanded={isMenuOpen}
        aria-controls={ariaControls}
        compact={true}
        color="neutral"
        icon={isMenuOpen ? <CloseIcon /> : <KebabMenuIcon />}
        onClick={onToggle}
        variant="plain"
      />
    </MenuToggleWrapper>
  )
}
